import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

const REDUCED = '(prefers-reduced-motion: reduce)'

/**
 * Site-wide motion, mounted once inside the router:
 * - reveals every [data-reveal] element as it scrolls into view (also ones added later, e.g. after data loads)
 * - tilts [data-tilt] elements towards the pointer, with a light sheen (styles/motion.css)
 * - draws a thin scroll-progress bar at the top of the page
 * Visitors who ask for reduced motion get everything shown at once, without movement.
 */
function MotionLayer() {
  const { pathname } = useLocation()
  const [progress, setProgress] = useState(0)

  // Reveal on scroll
  useEffect(() => {
    const root = document.documentElement
    if (window.matchMedia(REDUCED).matches || typeof IntersectionObserver === 'undefined') {
      root.classList.remove('motion-ready')
      return undefined
    }
    root.classList.add('motion-ready')

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('is-in')
          observer.unobserve(entry.target)
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    )
    const watch = (scope) => {
      scope.querySelectorAll?.('[data-reveal]:not(.is-in)').forEach((node) => observer.observe(node))
    }
    watch(document)

    // Content rendered later (route changes, products loading) is picked up too
    const mutations = new MutationObserver((records) => {
      records.forEach((record) =>
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return
          if (node.matches?.('[data-reveal]:not(.is-in)')) observer.observe(node)
          watch(node)
        })
      )
    })
    mutations.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      mutations.disconnect()
    }
  }, [])

  // Pointer tilt (desktop pointers only)
  useEffect(() => {
    if (window.matchMedia(REDUCED).matches || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return undefined
    let active = null
    let frame = 0

    const reset = (node) => {
      node.style.removeProperty('--rx')
      node.style.removeProperty('--ry')
      node.classList.remove('is-tilting')
    }
    const onMove = (e) => {
      const node = e.target.closest?.('[data-tilt]')
      if (active && active !== node) reset(active)
      active = node
      if (!node) return
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const rect = node.getBoundingClientRect()
        const x = (e.clientX - rect.left) / rect.width
        const y = (e.clientY - rect.top) / rect.height
        const strength = Number(node.dataset.tilt) || 6
        node.style.setProperty('--rx', `${((0.5 - y) * strength).toFixed(2)}deg`)
        node.style.setProperty('--ry', `${((x - 0.5) * strength).toFixed(2)}deg`)
        node.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`)
        node.style.setProperty('--my', `${(y * 100).toFixed(1)}%`)
        node.classList.add('is-tilting')
      })
    }
    const onLeave = () => {
      if (active) reset(active)
      active = null
    }
    document.addEventListener('pointermove', onMove, { passive: true })
    document.addEventListener('pointerleave', onLeave)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerleave', onLeave)
    }
  }, [])

  // Scroll progress
  useEffect(() => {
    let frame = 0
    const update = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight
        setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0)
      })
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [pathname])

  return <div className="fx-progress" style={{ transform: `scaleX(${progress})` }} aria-hidden="true" />
}

export default MotionLayer
